import { useState, useEffect, useRef, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import * as Sentry from '@sentry/react';

const RECONNECT_DELAYS = [0, 2000, 5000, 10000, 30000];

/**
 * Custom hook for router monitoring SignalR connection
 * Provides real-time updates for router metrics, availability, resources, and config changes
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.routerId - Router ID to subscribe to (optional, subscribes to all if not provided)
 * @param {Function} options.onMetricUpdate - Callback for metric updates
 * @param {Function} options.onAvailabilityChanged - Callback for availability changes
 * @param {Function} options.onRouterAvailabilityChanged - Callback for router-wide availability changes
 * @param {Function} options.onResourceSnapshotUpdated - Callback for resource snapshot updates (CPU, memory, disk gauges)
 * @param {Function} options.onConfigChanged - Callback for router config changes by another user
 * @param {boolean} options.enabled - Whether the connection should be active
 * @returns {Object} Connection state and methods
 */
const useRouterMonitoringSignalR = ({
  routerId = null,
  onMetricUpdate = null,
  onAvailabilityChanged = null,
  onRouterAvailabilityChanged = null,
  onResourceSnapshotUpdated = null,
  onConfigChanged = null,
  enabled = true
} = {}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const connectionRef = useRef(null);
  const callbacksRef = useRef({});

  // Update callbacks ref whenever they change (without recreating connection)
  useEffect(() => {
    callbacksRef.current = {
      onMetricUpdate,
      onAvailabilityChanged,
      onRouterAvailabilityChanged,
      onResourceSnapshotUpdated,
      onConfigChanged,
    };
  }, [onMetricUpdate, onAvailabilityChanged, onRouterAvailabilityChanged, onResourceSnapshotUpdated, onConfigChanged]);

  /**
   * Gets the auth token for SignalR connection
   */
  const getAccessToken = useCallback(() => {
    try {
      const storedUser = JSON.parse(localStorage.getItem('user'));
      return storedUser?.token || '';
    } catch (catchError) {
        Sentry.captureException(catchError);
      return '';
    }
  }, []);

  /**
   * Subscribe to a specific router's updates
   */
  const subscribeToRouter = useCallback(async (targetRouterId) => {
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      console.warn('Cannot subscribe to router: Not connected');
      return false;
    }

    try {
      await connectionRef.current.invoke('SubscribeToRouter', targetRouterId);
      console.log(`Subscribed to router ${targetRouterId}`);
      return true;
    } catch (error) {
        Sentry.captureException(error);
      console.error(`Error subscribing to router ${targetRouterId}:`, error);
      return false;
    }
  }, []);

  /**
   * Unsubscribe from a specific router's updates
   */
  const unsubscribeFromRouter = useCallback(async (targetRouterId) => {
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      console.warn('Cannot unsubscribe from router: Not connected');
      return false;
    }

    try {
      await connectionRef.current.invoke('UnsubscribeFromRouter', targetRouterId);
      console.log(`Unsubscribed from router ${targetRouterId}`);
      return true;
    } catch (error) {
        Sentry.captureException(error);
      console.error(`Error unsubscribing from router ${targetRouterId}:`, error);
      return false;
    }
  }, []);

  /**
   * Subscribe to all routers' updates
   */
  const subscribeToAllRouters = useCallback(async () => {
    if (!connectionRef.current || connectionRef.current.state !== signalR.HubConnectionState.Connected) {
      console.warn('Cannot subscribe to all routers: Not connected');
      return false;
    }

    try {
      await connectionRef.current.invoke('SubscribeToAllRouters');
      console.log('Subscribed to all routers');
      return true;
    } catch (error) {
        Sentry.captureException(error);
      console.error('Error subscribing to all routers:', error);
      return false;
    }
  }, []);

  /**
   * Initialize SignalR connection
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    let cancelled = false;
    let retryTimeout = null;

    // Build the connection.
    // Hub path is /api/hubs/routermonitoring so that the production Nginx /api/ location
    // block (which carries the WebSocket upgrade headers) correctly forwards the request
    // to the backend.  Placing hubs at the /hubs/ root causes Nginx to serve a 404 from
    // the static-file server instead of proxying to the .NET API.
    const connection = new signalR.HubConnectionBuilder()
      .withUrl('/api/hubs/routermonitoring', {
        accessTokenFactory: getAccessToken
      })
      .withAutomaticReconnect(RECONNECT_DELAYS)
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    // Setup event handlers - use callbacksRef to avoid stale closures
    connection.on('MetricUpdate', (data) => {
      setLastUpdate(new Date().toISOString());
      callbacksRef.current.onMetricUpdate?.(data);
    });

    connection.on('AvailabilityChanged', (data) => {
      setLastUpdate(new Date().toISOString());
      callbacksRef.current.onAvailabilityChanged?.(data);
    });

    connection.on('RouterAvailabilityChanged', (data) => {
      setLastUpdate(new Date().toISOString());
      callbacksRef.current.onRouterAvailabilityChanged?.(data);
    });

    connection.on('ResourceSnapshotUpdated', (data) => {
      setLastUpdate(new Date().toISOString());
      callbacksRef.current.onResourceSnapshotUpdated?.(data);
    });

    connection.on('ConfigChanged', (data) => {
      setLastUpdate(new Date().toISOString());
      callbacksRef.current.onConfigChanged?.(data);
    });

    // Connection lifecycle handlers
    connection.onclose((error) => {
      setIsConnected(false);
      if (error) {
        setConnectionError(error.message);
      }
    });

    connection.onreconnecting((error) => {
      setIsConnected(false);
      console.debug('Router monitoring SignalR reconnecting...', error);
    });

    connection.onreconnected(async (connectionId) => {
      console.debug('Router monitoring SignalR reconnected', connectionId);
      setIsConnected(true);
      setConnectionError(null);

      // Re-subscribe after reconnection
      try {
        if (routerId) {
          await connection.invoke('SubscribeToRouter', routerId);
        } else {
          await connection.invoke('SubscribeToAllRouters');
        }
      } catch (error) {
          Sentry.captureException(error);
        console.error('Error re-subscribing after reconnect:', error);
      }
    });

    // Start connection
    const startConnection = async () => {
      if (cancelled) return;

      try {
        await connection.start();
        if (cancelled) return;

        console.debug('Router monitoring SignalR connected');
        setIsConnected(true);
        setConnectionError(null);

        // Subscribe to router(s) after connection
        if (routerId) {
          await connection.invoke('SubscribeToRouter', routerId);
        } else {
          await connection.invoke('SubscribeToAllRouters');
        }
      } catch (error) {
          Sentry.captureException(error);
        if (cancelled) return;
        console.error('Router monitoring SignalR connection error:', error);
        setConnectionError(error.message);
        setIsConnected(false);
      }
    };

    startConnection();

    // Cleanup on unmount
    return () => {
      cancelled = true;
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      connection.stop().catch(err => console.debug('Error stopping router monitoring SignalR:', err));
      connectionRef.current = null;
    };
  }, [enabled, routerId, getAccessToken]);

  return {
    isConnected,
    connectionError,
    lastUpdate,
    subscribeToRouter,
    unsubscribeFromRouter,
    subscribeToAllRouters
  };
};

export default useRouterMonitoringSignalR;
